import { useMemo } from "react";

import { useIDPResults } from "../../../api/hooks/idpFd/useIDPResults";
import {
  DocType,
  IdpDocTypeFilterOptions,
  IdpFdDocumentsFilter,
  IdpFdDocumentsOrderBy,
  IdpStatusFilterOptions,
  OrderBy,
  Status,
} from "../../../graphql/generated";
import { IDPDocumentOption } from "../types";

interface UseIDPDocumentsForKYCProps {
  customerId?: number | null;
  documentType?: DocType;
}

export const useIDPDocumentsForKYC = ({
  customerId,
  documentType,
}: UseIDPDocumentsForKYCProps = {}) => {
  const filter: IdpFdDocumentsFilter = useMemo(() => {
    const filterObj: IdpFdDocumentsFilter = {
      status: {
        equals: Status.Completed as unknown as IdpStatusFilterOptions,
      },
    };

    if (customerId) {
      filterObj.customer = {
        id: {
          equals: customerId,
        },
      };
    }
    if (documentType) {
      filterObj.documentType = {
        equals: documentType as unknown as IdpDocTypeFilterOptions,
      };
    }

    return filterObj;
  }, [customerId, documentType]);

  const orderBy: IdpFdDocumentsOrderBy = {
    createdAt: OrderBy.Desc,
  };

  const [{ data: idpResponse, fetching: loading, error }] = useIDPResults({
    filter,
    orderBy,
    page: 1,
  });

  const options: IDPDocumentOption[] = useMemo(() => {
    if (!idpResponse?.idpFdDocuments?.data) return [];

    return idpResponse.idpFdDocuments.data.map((item) => ({
      id: item.id,
      label: `${item.documentType} - ${
        item.document?.filePath || `ID: ${item.id}`
      } (${new Date(item.createdAt || "").toLocaleDateString()})`,
      documentType: item.documentType,
      createdAt: item.createdAt || "",
    }));
  }, [idpResponse]);

  return {
    options,
    loading,
    error,
  };
};

// Specific hooks for each document type
export const useKTPDocuments = (customerId?: number | null) => {
  return useIDPDocumentsForKYC({
    customerId,
    documentType: DocType.Ktp,
  });
};

export const useNPWPCompanyDocuments = (customerId?: number | null) => {
  return useIDPDocumentsForKYC({
    customerId,
    documentType: DocType.NpwpCompany,
  });
};

export const useNPWPIndividuDocuments = (customerId?: number | null) => {
  return useIDPDocumentsForKYC({
    customerId,
    documentType: DocType.NpwpIndividu,
  });
};
